import React, { FC, useEffect, useMemo, useState } from "react"
import Loading from "../../../../ui/Loading"
import { KButton, KInput, KSpan, KTitleSpan, KTooltip } from "kahuna-base-react-components"
import { systems } from "../../../../../apis"
import { EmailTemplate } from "../../../../../types"
import { toast } from "react-toastify"
import {
  EMAIL_TEMPLATE_LANGUAGES,
  EMAIL_TEMPLATE_PLACEHOLDER,
  errorFilledToastOptions,
  successFilledToastOptions
} from "../../../../../constants"

type EmailTemplatesProps = {
  setOpenSidebar: (openSidebar: boolean) => void
  openSidebar: boolean
}

const EmailTemplates: FC<EmailTemplatesProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [templateList, setTemplateList] = useState<EmailTemplate[]>([])
  const [changedIdList, setChangedIdList] = useState<number[]>([])
  const [selectedLang, setSelectedLang] = useState<string>(EMAIL_TEMPLATE_LANGUAGES[0].value)
  const [openIdList, setOpenIdList] = useState<number[]>([])

  const fetchTemplateList = () => {
    setLoading(true)
    systems(`/email-template/`, "GET")
      .then((resp) => {
        setLoading(false)
        setTemplateList(resp.data.results)
        setChangedIdList([])
      })
      .catch(() => {
        setLoading(false)
        toast.error("Error fetching email templates", errorFilledToastOptions)
      })
  }

  useEffect(() => {
    fetchTemplateList()
  }, [])

  const visibleTemplateList = useMemo(
    () => templateList.filter((template) => template.lang === selectedLang),
    [templateList, selectedLang]
  )

  const groupedTemplateList = useMemo(() => {
    const groupList: { group: string; templateList: EmailTemplate[] }[] = []
    visibleTemplateList.forEach((template) => {
      const group = template.group || "Other"
      const existingGroup = groupList.find((item) => item.group === group)
      if (existingGroup) {
        existingGroup.templateList.push(template)
      } else {
        groupList.push({ group, templateList: [template] })
      }
    })
    return groupList
  }, [visibleTemplateList])

  const onFieldChange = (templateId: number, field: "subject" | "body" | "cc", value: string) => {
    setChangedIdList((prevList) => Array.from(new Set([...prevList, templateId])))
    setTemplateList((prevList) =>
      prevList.map((template) => (template.id === templateId ? { ...template, [field]: value } : template))
    )
  }

  const toggleOpen = (templateId: number) => {
    setOpenIdList((prevList) =>
      prevList.includes(templateId) ? prevList.filter((id) => id !== templateId) : [...prevList, templateId]
    )
  }

  const onSave = () => {
    const updatedList = templateList
      .filter((template) => changedIdList.includes(template.id))
      .map((template) => ({ id: template.id, subject: template.subject, body: template.body, cc: template.cc }))

    if (updatedList.some((template) => !template.subject.trim() || !template.body.trim())) {
      toast.error("Subject and body cannot be empty", errorFilledToastOptions)
      return
    }

    setLoading(true)
    systems(`/email-template/save/`, "PUT", updatedList)
      .then((resp) => {
        setLoading(false)
        if (resp.data.success) {
          toast.success("Successfully saved email templates", successFilledToastOptions)
          setChangedIdList([])
        } else {
          toast.error("Error saving email templates", errorFilledToastOptions)
        }
      })
      .catch(() => {
        setLoading(false)
        toast.error("Error saving email templates", errorFilledToastOptions)
      })
  }

  const renderTemplate = (template: EmailTemplate) => {
    const isOpen = openIdList.includes(template.id)

    return (
      <div key={template.id}>
        <div
          className="w-full flex justify-between items-center pb-2 cursor-pointer"
          style={{ borderBottom: "1px solid #111111" }}
          onClick={() => toggleOpen(template.id)}
        >
          <div className="flex items-center gap-2">
            <KButton
              icon={isOpen ? "/admin_icons/caret-up.svg" : "/admin_icons/caret-down.svg"}
              width="24px"
              height="24px"
              background="transparent"
              shadowDisabled={true}
              padding="0px"
              onClick={() => {}}
            />
            <div className="flex flex-col gap-1">
              <KTitleSpan fontSize={18} lineHeight="auto" text={template.name} />
              {template.description && (
                <KSpan text={template.description} fontSize={12} lineHeight="16px" color="#6B7280" />
              )}
            </div>
          </div>
          <KSpan text={`Updated: ${template.updated?.substring(0, 10) || "-"}`} fontSize={12} color="#6B7280" />
        </div>
        {isOpen && (
          <div className="flex flex-col gap-3 mt-4">
            <div className="flex flex-col gap-1">
              <KSpan text="Subject" color="#111" fontWeight={600} />
              <KInput
                data-testid={`email-template-subject-${template.id}`}
                value={template.subject}
                onChange={(text) => onFieldChange(template.id, "subject", text)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <KSpan text="CC" color="#111" fontWeight={600} />
              <KInput
                placeholder="comma seprated for multiple email adress. Ex: barin@kahuna.com.tr,ozmen@kahuna.com.tr"
                data-testid={`email-template-cc-${template.id}`}
                value={template.cc || ""}
                onChange={(text) => onFieldChange(template.id, "cc", text)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <KSpan text="Body" color="#111" fontWeight={600} />
              <textarea
                data-testid={`email-template-body-${template.id}`}
                className="border rounded-[10px] p-3 w-[100%] text-sm"
                style={{ minHeight: 260, fontFamily: "inherit" }}
                value={template.body}
                onChange={(event) => onFieldChange(template.id, "body", event.target.value)}
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8" style={{ paddingTop: 22 }}>
      <div className="flex justify-between items-center mb-6">
        <KTooltip
          backgroundColor="#000"
          padding="0px 4px"
          height="20px"
          arrowColor="#000"
          position="bottom"
          content={
            <div className="h-5 w-max flex items-center">
              <KSpan
                text={props.openSidebar ? "Hide sidebar" : "Open sidebar"}
                fontSize={12}
                lineHeight="16px"
                color="#FFF"
              />
            </div>
          }
        >
          <div className="flex items-center">
            <KButton
              dataTestId="reports-sidebar-toggle-button"
              icon="/left-sidebar.svg"
              width="32px"
              height="32px"
              background="transparent"
              shadowDisabled={true}
              padding="6px"
              onClick={() => props.setOpenSidebar(!props.openSidebar)}
            />
            <KTitleSpan fontSize={20} lineHeight="28px" text="Email Templates" />
          </div>
        </KTooltip>
        <div className="flex gap-2">
          {EMAIL_TEMPLATE_LANGUAGES.map((language) => (
            <KButton
              key={language.value}
              width="110px"
              height="35px"
              text={language.label}
              background={selectedLang === language.value ? "black" : "white"}
              textColor={selectedLang === language.value ? "white" : "#111"}
              onClick={() => setSelectedLang(language.value)}
            />
          ))}
        </div>
      </div>
      <div className="w-[100%] p-4">
        {loading && <Loading />}
        <div className="mb-6 p-3 rounded-[10px]" style={{ backgroundColor: "#F7F7F7" }}>
          <KSpan text="Available placeholders" color="#111" fontWeight={600} />
          <div className="mt-1">
            <KSpan
              text={`${Object.values(EMAIL_TEMPLATE_PLACEHOLDER).join(
                ", "
              )} — these are filled automatically when the email is prepared on the Takedown Logs page.`}
              fontSize={12}
              lineHeight="16px"
              color="#6B7280"
            />
          </div>
        </div>
        {groupedTemplateList.map(({ group, templateList: groupTemplateList }) => (
          <div key={group} className="mb-6 rounded-[10px] border border-solid p-4" style={{ borderColor: "#D1D5DB" }}>
            <KSpan text={group} color="#111" fontWeight={600} />
            <div className="flex flex-col gap-6 mt-4">
              {groupTemplateList.map((template) => renderTemplate(template))}
            </div>
          </div>
        ))}
        {!loading && visibleTemplateList.length === 0 && <KSpan text="No email templates yet" color="#6B7280" />}
        <div className="flex justify-end">
          <KButton
            width="120px"
            dataTestId="email-templates-save-button"
            disabled={changedIdList.length === 0}
            onClick={() => onSave()}
            text="Save"
          />
        </div>
      </div>
    </div>
  )
}

export default EmailTemplates
